import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Distribution, Proposal } from "rsl-api-106/lib/types";

import Button from "@components/_Layout/Controls/Button";
import ReactComment from "@components/_Layout/ReactComment";
import ButtonAddToFavorites from "@components/Favorites/ButtonAddToFavorites";
import { useCart, useConfig, useLoadingScreen, useSession } from "@hooks";
import { routes } from "@routes";
import { getCurrencySymbol } from "@utils/currencies";
import { replaceImgOnError } from "@utils/dom";
import { showFavoritesProposal } from "@utils/favorites";
import { getDiscountByPrices } from "@utils/prices";

import ProposalDate from "../ProposalDate";

const languagesVarsCodes = [
  "LANG_neobooking_please_wait", //Veuillez patienter quelques instants
  "LANG_neobooking_proposal_adding_in_progress", //Ajout de la réservation en cours
];

type DistributionProps = {
  distribution: Distribution;
};

const ProposalTitle: React.FC<DistributionProps> = ({ distribution }) => {
  return (
    <span className="tw-block">
      {distribution?.roomTypes?.map((item, index) => {
        return (
          <span key={`dr_${index}`}>
            {item?.quantity}x{" "}
            {`${item?.label} ${
              index + 1 !== distribution?.roomTypes?.length ? " + " : ""
            }`}
          </span>
        );
      })}
    </span>
  );
};

interface ProposalPaxProps extends DistributionProps {
  singlePax: string;
  plurialPax: string;
}

const ProposalPax: React.FC<ProposalPaxProps> = ({
  distribution,
  singlePax,
  plurialPax,
}) => {
  let total = 0;
  distribution.roomTypes?.forEach((r) => {
    total += r?.paxMax!! * r?.quantity!!;
  });

  return (
    <span className="tw-block tw-lowercase">
      {total} {<>{total > 1 ? plurialPax : singlePax}</>}
    </span>
  );
};

type ProposalProps = {
  proposal: Proposal;
  proposalActionText: string;
  singlePax: string;
  plurialPax: string;
};

const ProposalItem: React.FC<ProposalProps> = ({
  proposal,
  proposalActionText,
  singlePax,
  plurialPax,
}) => {
  /* Hooks */
  const { addProposalFromKeys } = useCart();
  const { getLanguageVarValue } = useSession();
  const navigate = useNavigate();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const priceColorClass = mode === "vacanceole" ? "tw-text-v_blue-dark" : "tw-text-primary_corseole-500";

  /* State */
  const [onLoadingChooseProposal, setOnLoadingChooseProposal] =
    useState<boolean>(false);

  const handleChooseProposalFromKey = (proposalKey: string) => {
    setOnLoadingChooseProposal(true);
    addProposalFromKeys([proposalKey], undefined, (err) => {
      if (err) toast.error(err.message);
      else {
        //une fois le add proposal key effectué, on redirige vers la page de la reservation
        navigate(routes.reservation, {
          state: null,
          replace: true,
        });
      }
      setOnLoadingChooseProposal(false);
    });
  };

  useLoadingScreen(
    onLoadingChooseProposal,
    `${getLanguageVarValue("LANG_neobooking_proposal_adding_in_progress")}...`,
    getLanguageVarValue("LANG_neobooking_please_wait")
  );

  return (
    <>
      <ReactComment text={`proposalKey: ${proposal.proposalKey}`} />
      <div
        className={`tw-grid tw-grid-cols-12 tw-border-t tw-border-gray-100 tw-bg-white tw-font-montserrat ${
          proposal.isCounterProposal ? "tw-border tw-border-yellow-300" : ""
        }`}
      >
        <div className="tw-col-span-12 sm:tw-col-span-4 lg:tw-col-span-3">
          <div className="tw-overflow-hidden tw-h-full tw-w-full">
            <img
              className="tw-w-full tw-h-full tw-object-cover hover:tw-scale-105 tw-transition-all tw-duration-200 tw-ease-in-out"
              src={proposal?.imagePath ?? ""}
              onError={(e) => replaceImgOnError(e)}
              style={{ maxHeight: "250px" }}
            />
          </div>
        </div>
        <div className="tw-col-span-12 sm:tw-col-span-8 lg:tw-col-span-9">
          <div className="tw-h-full tw-p-2 tw-flex tw-flex-col tw-justify-between">
            <div>
              <div className="tw-flex tw-justify-between">
                <div className={`tw-font-semibold ${priceColorClass}`}>
                  <ProposalTitle distribution={proposal.distribution!!} />
                </div>
                <div className="tw-flex tw-items-center">
                  {/*Discount %*/}
                  {proposal?.priceWithoutDiscount?.amount !==
                    proposal?.price?.amount && (
                    <div className="tw-text-secondary-500 tw-text-2xl tw-font-semibold">
                      -
                      {getDiscountByPrices(
                        proposal?.price?.amount!!,
                        proposal?.priceWithoutDiscount?.amount!!
                      )}
                      %
                    </div>
                  )}
                  {showFavoritesProposal() && (
                    <ButtonAddToFavorites
                      mode="proposal"
                      proposalKey={proposal.proposalKey!!}
                    />
                  )}
                </div>
              </div>
              <div className="tw-font-semibold tw-mb-3">
                <ProposalDate
                  startDate={new Date(proposal.startDate)}
                  endDate={new Date(proposal.endDate)}
                />
              </div>
              <div className="tw-w-4/5">
                <p className="tw-mb-2 tw-leading-5">
                  {/*Total Pax max*/}
                  <ProposalPax
                    singlePax={singlePax}
                    plurialPax={plurialPax}
                    distribution={proposal.distribution!!}
                  />
                  {/*La description de la chambre*/}
                  {proposal.distribution?.roomTypes![0]?.description}
                  &nbsp;
                </p>
              </div>
            </div>
            <div className="tw-mt-1">
              <div className="tw-flex tw-justify-end tw-items-center">
                {proposal?.priceWithoutDiscount?.amount !==
                  proposal?.price?.amount && (
                  <div className="tw-line-through tw-mr-1 tw-text-gray-600">
                    {proposal?.priceWithoutDiscount?.amount?.toFixed(2)}
                    {getCurrencySymbol(
                      proposal?.price?.currencyCode?.toUpperCase()!!
                    )}
                  </div>
                )}
                <div className={`tw-mr-3 tw-text-2xl tw-font-semibold ${priceColorClass}`}>
                  {proposal?.price?.amount?.toFixed(2)}
                  {getCurrencySymbol(
                    proposal?.price?.currencyCode?.toUpperCase()!!
                  )}
                </div>
                <Button
                  shape="pills"
                  title={proposal?.proposalKey ?? ""}
                  isLoading={onLoadingChooseProposal}
                  type="button"
                  onClick={() =>
                    handleChooseProposalFromKey(proposal?.proposalKey!!)
                  }
                >
                  {proposalActionText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ProposalItem.propTypes = {
  //
};

export default ProposalItem;
